import React, { useEffect, useMemo, useState } from "react";
import { complianceApi } from "@/lib/api";
import { PageShell } from "./Overview";
import { Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "filing", label: "Filings" },
  { id: "tax", label: "Tax" },
  { id: "vat", label: "VAT / GST" },
  { id: "kyc", label: "KYC" },
  { id: "audit", label: "Audit" },
];

const Compliance = () => {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => complianceApi.list().then(setTasks).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? tasks : tasks.filter((t) => t.category === filter)),
    [tasks, filter]
  );

  const toggle = async (t) => {
    const next = t.status === "completed" ? "pending" : "completed";
    await complianceApi.update(t.id, next);
    toast.success(next === "completed" ? "Marked completed" : "Reopened");
    load();
  };

  const overdue = tasks.filter((t) => t.status !== "completed" && new Date(t.due_date) < new Date()).length;
  const upcoming30 = tasks.filter((t) => {
    if (t.status === "completed") return false;
    const d = new Date(t.due_date);
    const now = new Date();
    const soon = new Date(); soon.setDate(now.getDate() + 30);
    return d >= now && d <= soon;
  }).length;

  return (
    <PageShell title="Compliance" subtitle={`${tasks.length} tracked obligations`}>
      <div className="grid grid-cols-3 gap-px bg-border-subtle border border-border-subtle mb-8">
        <Mini label="Overdue" value={overdue} danger />
        <Mini label="Due in 30 days" value={upcoming30} />
        <Mini label="Completed" value={tasks.filter((t) => t.status === "completed").length} />
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="compliance-filters">
        {CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setFilter(c.id)}
            data-testid={`filter-${c.id}`}
            className={`px-4 py-2 text-xs font-mono-accent uppercase tracking-[0.18em] border transition-colors ${
              filter === c.id
                ? "border-artha-amber text-artha-amber"
                : "border-border-subtle text-ink-muted hover:text-ink-body"
            }`}>
            {c.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border-subtle p-16 text-center">
          <div className="text-ink-muted text-sm">No compliance tasks yet. Tasks populate when an entity becomes active.</div>
        </div>
      ) : (
        <div className="border border-border-subtle divide-y divide-border-subtle">
          {filtered.map((t) => {
            const isOverdue = t.status !== "completed" && new Date(t.due_date) < new Date();
            return (
              <div key={t.id} className="grid grid-cols-12 gap-4 items-center px-5 py-4" data-testid={`task-row-${t.id}`}>
                <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`}
                  className={`col-span-1 w-6 h-6 border flex items-center justify-center transition-colors ${
                    t.status === "completed"
                      ? "bg-artha-amber border-artha-amber"
                      : "border-border-subtle hover:border-artha-amber"
                  }`}>
                  {t.status === "completed" && <Check size={14} className="text-artha-ink" />}
                </button>
                <div className="col-span-6">
                  <div className={`text-sm ${t.status === "completed" ? "line-through text-ink-muted" : ""}`}>{t.title}</div>
                  <div className="text-xs text-ink-muted mt-1 leading-relaxed">{t.description}</div>
                </div>
                <div className="col-span-2 text-xs font-mono-accent uppercase tracking-[0.18em] text-ink-muted">
                  {t.category} · {t.priority}
                </div>
                <div className="col-span-3 text-right font-mono-accent text-sm">
                  {isOverdue ? (
                    <span className="inline-flex items-center gap-1 text-[#C94F4F]"><AlertTriangle size={12} />{new Date(t.due_date).toLocaleDateString()}</span>
                  ) : (
                    <span className={t.status === "completed" ? "text-ink-muted" : "text-artha-amber"}>{new Date(t.due_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
};

const Mini = ({ label, value, danger }) => (
  <div className="bg-surface-card p-6">
    <div className="text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted">{label}</div>
    <div className={`h-display text-4xl mt-3 ${danger && value > 0 ? "text-[#C94F4F]" : "text-ink-body"}`}>{value}</div>
  </div>
);

export default Compliance;
